import { useContext, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import textActions from "actions/text";
import { convertRecipientAccountVariables, convertRecipientContactVariables } from "utils/TextFormUtils";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import TextingComponents from "shared/TextingComponents";
import CustomPrompt from "shared/confirmation/CustomPrompt";
import { TextingComponentContext } from "shared/context/TextingComponentProvider";
import { useConfirm } from "shared/confirmation/useConfirm";

function TextHistory() {
    const dispatch = useDispatch();
    const textHistoryConversationRecipient = useSelector(state => state.text.textHistoryConversationRecipient);
    const textHistoryConversationSender = useSelector(state => state.text.textHistoryConversationSender);
    const recipientId = useSelector(state => state.text.textHistoryRecipientId);
    const recipientType = useSelector(state => state.text.textHistoryRecipientType);
    const recipientName = useSelector(state => state.text.textHistoryRecipientName);
    const [params] = useSearchParams();

    useEffect(() => {
        const to = params.get('to')?.split(",");
        const from = params.get('from')?.split(",");
        dispatch(textActions.getTextHistoryConversations({
            contact_phone: to?.length ? to : undefined,
            search_phone: params.get('search') || undefined,
            page: params.get('page') || 1,
            limit: 25,
            customer_phone: from?.length ? from : undefined
        }));
    }, [dispatch, params]);

    useEffect(() => {
        dispatch(textActions.getPhones());
        dispatch(textActions.getContactPhones());
    }, [dispatch]);

    const { textHistoryConversations = [], textHistoryConversations_count = 0, isReadingTextHistoryConversations, contactPhones = [] } = useSelector(state => state.text);
    const { filteredMessagesForTextHistory = [], isReadingMessagesForTextHistory } = useSelector(state => state.text);

    const getConversationBeingViewed = (contact_phone, customer_phone) => {
        return textHistoryConversations.find(conversation => conversation.contact_phone === contact_phone && conversation.customer_phone === customer_phone) || {};
    }

    const onRowClick = (conversation) => {
        const { contact_phone, customer_phone } = conversation;
        const conversationBeingViewed = getConversationBeingViewed(contact_phone, customer_phone);
        const recipientType = conversationBeingViewed.recipientInfo?.contact_type;
        const recipientId = conversationBeingViewed.recipientInfo?.id;
        dispatch(textActions.setConversationBeingViewedInTextHistory({ contact_phone, customer_phone, recipientId, recipientType, recipientName: conversationBeingViewed.recipientInfo?.name }));
        dispatch(textActions.getFilteredMessagesForTextHistory({ contact_phone, customer_phone, contact_id:recipientId }));
        if (recipientType) {
            if (recipientType === 'account') {
                dispatch(textActions.getAccountInfo(recipientId));
            } else if (recipientType === 'person') {
                dispatch(textActions.getContactInfo(recipientId));
            }
            dispatch(textActions.getRecipientPhones(recipientId));
        } else {
            dispatch(textActions.clearRecipientPhones());
        }
    };

    const conversationBeingViewed = getConversationBeingViewed(textHistoryConversationRecipient, textHistoryConversationSender);
    const recipientPhones = useSelector(state => state.text.recipientPhones);
    const isLoadingRecipientDetails = useSelector(state => state.text.isReadingRecipientPhones);
    const userOptions = useSelector(state => state.text.textHistoryUserOptions);

    const detailsOfContactRecipient = useSelector(state => state.text.contact);
    const detailsOfAccountRecipient = useSelector(state => state.text.account);

    const page = params.get('page') || 1;

    const currentQuery = Object.fromEntries(params);
    const navigate = useNavigate();
    const location = useLocation();
    const { isTextFormDirty } = useContext(TextingComponentContext)
    const { isConfirmed } = useConfirm();
    const isArchivedRecord = conversationBeingViewed?.recipientInfo?.archived;

    CustomPrompt(null, isTextFormDirty, isConfirmed, 'Text will not be saved.', 'Are you sure you want to exit?');
    return (
            <TextingComponents
                filteredMessages={filteredMessagesForTextHistory}
                isReadingFilteredMessages={isReadingMessagesForTextHistory}
                messages={textHistoryConversations}
                messagesCount={textHistoryConversations_count}
                loading={isReadingTextHistoryConversations}
                onPageChange={(_, { activePage }) => {
                    navigate(`${location.pathname}?${new URLSearchParams({ ...currentQuery, page: activePage }).toString()}`)
                }}
                activePage={page}
                onRowClick={onRowClick}
                recipientId={recipientId}
                recipientName={recipientName || "Unknown"}
                isPanel
                recipientOptions={recipientPhones}
                isLoadingRecipientDetails={isLoadingRecipientDetails}
                selectedRow={conversationBeingViewed.id}
                userOptions={userOptions}
                onReadMessages={(contact_phone, customer_phone) => {
                    dispatch(textActions.getFilteredMessagesForTextHistory({ contact_phone, customer_phone }));
                    dispatch(textActions.getTextHistoryConversations({ page, limit: 25 }));
                    dispatch(textActions.setConversationBeingViewedInTextHistory({ contact_phone, customer_phone, recipientName, recipientId, recipientType }));
                }}
                onReplaceRecipientVariable={(variable) => {
                    if (recipientType === 'person') {
                        return convertRecipientContactVariables(variable, detailsOfContactRecipient);
                    }
                    if (recipientType === 'account') {
                        return convertRecipientAccountVariables(variable, detailsOfAccountRecipient);
                    }
                    return variable;
                }}
                isAccount={recipientType === 'account'}
                contact_phone={textHistoryConversationRecipient}
                customer_phone={textHistoryConversationSender}
                onChangeNumbers={(payload) => {
                    dispatch(textActions.setConversationBeingViewedInTextHistory({ ...payload, recipientId, recipientName, recipientType }));
                    dispatch(textActions.getFilteredMessagesForTextHistory(payload));
                }}
                onFilter={(_, { name, value }) => {
                    const isSearching = name === 'search' && value;
                    const { search, ...updatedQuery } = currentQuery;
                    const query = new URLSearchParams(isSearching ? { search: value } : { ...updatedQuery, [name]: value, page: 1 });
                    if (!(value?.length || value)) {
                        query.delete(name);
                    }
                    const queryString = query.toString();
                    navigate(`${location.pathname}${queryString ? `?${queryString}` : ''}`)
                }}
                selectedRecipientFilters={params.get('to')?.split(",") || []}
                selectedSenderFilters={params.get('from')?.split(",") || []}
                recipientFilterOptions={contactPhones.map(phone => ({ phone }))}
                search={params.get('search') || ''}
                isArchivedRecord={isArchivedRecord}
            />
    );
}

export default TextHistory;
